/**
 * Semantic identifiers for client-raised alerts, passed to `alertStore` as `alertKey` and rendered
 * as `data-alert-key` on the toast.
 *
 * These exist so automated tests can assert on *which* alert fired without matching its text. The
 * displayed copy is translated, so text assertions are locale-dependent and break whenever the copy
 * changes — a class of breakage that has already cost a real bug hunt (sigholm/aurora-e2e#67).
 *
 * Rules for adding one:
 *
 * - **Reuse a backend code where one exists.** Errors carrying a canonical envelope `code` should
 *   report that code, not a value from here — see `elplan.errorAlert.helper.ts`. Do not create a second
 *   identifier for a condition the backend already names.
 * - Describe the *condition*, not the wording. `production-plan-published`, not
 *   `a-production-plan-has-been-published`.
 * - Only add keys that something actually selects on. This is not an exhaustive catalogue of the
 *   238 alert call sites; the great majority need no key, and alerts built from server error text
 *   have no stable identity to report anyway.
 */
export const ALERT_KEYS = {
  productionPlanPublished: 'production-plan-published',
  productionPlanOptimizationFinished: 'production-plan-optimization-finished',
  productionPlanOptimizationFailed: 'production-plan-optimization-failed',
  productionPlanPublishFailed: 'production-plan-publish-failed',
  draftProductionPlanOptimizationFailed: 'draft-production-plan-optimization-failed',
  elplanGatewayTimeout: 'elplan-gateway-timeout',
  eventSubmitted: 'event-submitted',
  eventSavedAsDraft: 'event-saved-as-draft',
  eventActivated: 'event-activated',
  eventChangeRequestApproved: 'event-change-request-approved',
} as const

export type AlertKey = (typeof ALERT_KEYS)[keyof typeof ALERT_KEYS]
